import React, { useEffect, useState } from 'react';
import {
  Modal, Form, Input, Button, Checkbox, Space, Divider, Spin, notification,
} from 'antd';
import { LockOutlined, MailOutlined, UserOutlined } from '@ant-design/icons';
import axios from './axiosConfig';

const ProfileModal = ({ open, onClose, loggedInUser, setLoggedInUser }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [allCategories, setAllCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [accountEmail, setAccountEmail] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    axios.get('/profile')
      .then((r) => {
        if (cancelled) return;
        const data = r.data || {};
        const cats = data.allCategories || [];
        setAllCategories(cats);
        const selected = data.notifyAll
          ? [...cats]
          : (data.notifyCategories || []);
        setSelectedCategories(selected);
        setAccountEmail(data.email || data.username || '');
        form.setFieldsValue({
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        });
      })
      .catch(() => {
        notification.error({ message: 'Nepodarilo sa načítať profil.' });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, form]);

  const selectAll = () => setSelectedCategories([...allCategories]);
  const deselectAll = () => setSelectedCategories([]);

  const handleSave = async (values) => {
    if (values.newPassword && values.newPassword !== values.confirmPassword) {
      notification.error({ message: 'Nové heslo a potvrdenie sa nezhodujú.' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        firstName: values.firstName,
        lastName: values.lastName,
        notifyCategories: selectedCategories,
      };
      if (values.currentPassword || values.newPassword) {
        payload.currentPassword = values.currentPassword;
        payload.newPassword = values.newPassword;
      }
      const { data } = await axios.put('/profile', payload);
      setLoggedInUser({
        ...(loggedInUser || {}),
        id: data.id,
        username: data.username,
        role: data.role,
        firstName: data.firstName || null,
        lastName: data.lastName || null,
        email: data.email,
      });
      form.setFieldsValue({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      notification.success({ message: 'Profil bol uložený.' });
      onClose();
    } catch (error) {
      const msg = error?.response?.data?.message || 'Profil sa nepodarilo uložiť.';
      notification.error({ message: msg });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={saving ? undefined : onClose}
      footer={null}
      centered
      width={560}
      className="profile-modal"
      destroyOnClose
      title="Môj profil"
      closable={!saving}
      maskClosable={!saving}
    >
      {loading ? (
        <div className="profile-modal-loading">
          <Spin />
        </div>
      ) : (
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
          requiredMark={false}
          className="profile-modal-form"
        >
          <p className="profile-modal-login">
            Prihlasovacie meno: <strong>{loggedInUser?.username}</strong>
          </p>

          <Form.Item name="firstName" label="Meno">
            <Input prefix={<UserOutlined />} placeholder="Meno" disabled={saving} />
          </Form.Item>
          <Form.Item name="lastName" label="Priezvisko">
            <Input prefix={<UserOutlined />} placeholder="Priezvisko" disabled={saving} />
          </Form.Item>
          <Form.Item label="E-mail">
            <Input
              prefix={<MailOutlined />}
              value={accountEmail || loggedInUser?.email || loggedInUser?.username || ''}
              disabled
              readOnly
            />
          </Form.Item>

          <Divider orientation="left" plain className="profile-modal-divider">
            Zmena hesla
          </Divider>
          <Form.Item name="currentPassword" label="Aktuálne heslo">
            <Input.Password prefix={<LockOutlined />} placeholder="••••••••" disabled={saving} />
          </Form.Item>
          <Form.Item name="newPassword" label="Nové heslo">
            <Input.Password prefix={<LockOutlined />} placeholder="aspoň 6 znakov" disabled={saving} />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            label="Potvrdenie nového hesla"
            dependencies={['newPassword']}
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!getFieldValue('newPassword') || !value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Heslá sa nezhodujú'));
                },
              }),
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="••••••••" disabled={saving} />
          </Form.Item>

          <Divider orientation="left" plain className="profile-modal-divider">
            Projekty na notifikácie
          </Divider>
          <p className="profile-notify-hint">
            Predvolene ste prihlásený na všetky projekty a aj na nové systémy.
            Odškrtnutím obmedzíte e-maily (nová požiadavka, zmena stavu, komentár) len na označené projekty.
          </p>
          <Space style={{ marginBottom: 12 }} wrap>
            <Button size="small" className="profile-ghost-btn" onClick={selectAll} disabled={saving || !allCategories.length}>
              Označiť všetky
            </Button>
            <Button size="small" className="profile-ghost-btn" onClick={deselectAll} disabled={saving || !allCategories.length}>
              Odznačiť všetky
            </Button>
          </Space>
          {allCategories.length === 0 ? (
            <p className="profile-notify-empty">Zatiaľ nie sú evidované žiadne projekty (kategórie).</p>
          ) : (
            <Checkbox.Group
              className="profile-notify-list"
              value={selectedCategories}
              onChange={(vals) => setSelectedCategories(vals)}
              disabled={saving}
              options={allCategories.map((c) => ({ label: c, value: c }))}
            />
          )}

          <div className="profile-modal-actions">
            <Button className="profile-ghost-btn" onClick={onClose} disabled={saving}>
              Zrušiť
            </Button>
            <Button type="primary" htmlType="submit" loading={saving}>
              Uložiť
            </Button>
          </div>
        </Form>
      )}
    </Modal>
  );
};

export default ProfileModal;
